import React, { useMemo, useState } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerView } from '@divvy-web/skylab.drawer'
import Spinner from '@divvy-web/skylab.spinner'
import Tab from '@divvy-web/skylab.tab'

import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import ApplicationDetailsTabContent from './ApplicationDetailsTabContent'
import DocumentsTabContent from './DocumentsTabContent'
import { useGetCreditApplicationDetails } from './GetCreditApplicationDetails.gql'
import styles from './styles.module.scss'

export interface DashboardDetailsDrawerProps {
  isShowing: boolean
  salesforceCreditId: string
  onClose: () => void
}

export type DrawerTabs = 'details' | 'documents'

const DashboardDetailsDrawer = ({ salesforceCreditId, isShowing, onClose }: DashboardDetailsDrawerProps) => {
  const [getClassName, makeTestId] = useNameFormatter('DashboardDetailsDrawer')
  const [activeTab, setActiveTab] = useState<DrawerTabs>('details')
  const { track } = useWebTracking()

  const onCloseDrawer = () => {
    switch (activeTab) {
      case 'documents':
        track('se_signup__application_dashboard_documents_tab__closed')
        break
      default:
        break
    }

    track('se_signup__application_dashboard_application_details_drawer_close__fired')
    onClose()
  }

  const { data, loading } = useGetCreditApplicationDetails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
    skip: !isShowing || !salesforceCreditId,
  })
  const creditApplication = data?.creditApplication

  const legalBusinessName = creditApplication?.businessInfo?.legalBusinessName || ''

  const tabContent = useMemo(() => {
    if (loading || !creditApplication) {
      return <Spinner centered />
    }

    switch (activeTab) {
      case 'documents':
        return <DocumentsTabContent salesforceCreditId={salesforceCreditId} />
      case 'details':
      default:
        return (
          <ApplicationDetailsTabContent
            creditApplication={creditApplication}
            setActiveTabToDocuments={() => setActiveTab('documents')}
          />
        )
    }
  }, [activeTab, creditApplication, loading, salesforceCreditId])

  return (
    <Drawer
      activeViewKey='default'
      className={`${styles[getClassName('')]} fs-unmask`}
      dataTestId={makeTestId(salesforceCreditId)}
      isShowing={isShowing}
      views={{
        default: (
          <DrawerView
            title={legalBusinessName}
            onCloseClick={onCloseDrawer}
          >
            <div className={styles[getClassName('drawer-view')]}>
              <div className={styles[getClassName('tabs')]}>
                <Tab
                  isActive={activeTab === 'details'}
                  onClick={() => {
                    track('se_signup__application_dashboard_application_details_tab_drawer__fired')
                    setActiveTab('details')
                  }}
                >
                  <FormattedMessage
                    defaultMessage='Application details'
                    id='sputnik.DashboardDetailsDrawer__QD4yJl'
                  />
                </Tab>
                <Tab
                  isActive={activeTab === 'documents'}
                  onClick={() => {
                    track('se_signup__application_dashboard_application_details_drawer_documents__fired')
                    setActiveTab('documents')
                  }}
                >
                  <FormattedMessage
                    defaultMessage='Documents'
                    id='sputnik.DashboardDetailsDrawer__vBlT6y'
                  />
                </Tab>
              </div>
              <div className={styles[getClassName('content')]}>{tabContent}</div>
            </div>
          </DrawerView>
        ),
      }}
    />
  )
}

export default DashboardDetailsDrawer
